<template>
  <div
    class="vdr"
    @mousedown="elmDown"
    ref="vdr"
  >
    <template v-if="resizeable">
      <div
        v-for="handle in handles"
        class="handle"
        :key="handle"
        :class="'handle-' + handle"
        :style="{ display: enabled ? 'block' : 'none'}"
        @mousedown.stop.prevent="handleDown(handle)"
        @pointerdown.stop=""
      />
    </template>
    <slot />
  </div>
</template>

<script>
export default {
  name: 'DraggableResizeable',
  props: {
    target: {
      type: Object,
      default: undefined
    },
    active: {
      type: Boolean, default: false
    },
    draggable: {
      type: Boolean, default: true
    },
    resizeable: {
      type: Boolean, default: true
    },
    w: {
      type: Number,
      default: 200,
      validator: function (val) {
        return val > 0
      }
    },
    h: {
      type: Number,
      default: 200,
      validator: function (val) {
        return val > 0
      }
    },
    minw: {
      type: Number,
      default: 50,
      validator: function (val) {
        return val >= 0
      }
    },
    minh: {
      type: Number,
      default: 50,
      validator: function (val) {
        return val >= 0
      }
    },
    x: {
      type: Number,
      default: 0,
      validator: function (val) {
        return typeof val === 'number'
      }
    },
    y: {
      type: Number,
      default: 0,
      validator: function (val) {
        return typeof val === 'number'
      }
    },
    handles: {
      type: Array,
      default: function () {
        return ['tl', 'tm', 'tr', 'mr', 'br', 'bm', 'bl', 'ml']
      },
      validator: function (val) {
        var s = new Set(['tl', 'tm', 'tr', 'mr', 'br', 'bm', 'bl', 'ml'])
        return new Set(val.filter(h => s.has(h))).size === val.length
      }
    },
    dragHandle: {
      type: String,
      default: null
    },
    dragCancel: {
      type: String,
      default: null
    },
    grid: {
      type: Array,
      default: function () {
        return [1, 1]
      }
    },
    parent: {
      type: Boolean, default: false
    }
  },

  created () {
    this.parentX = null
    this.parentW = null
    this.parentY = null
    this.parentH = null

    this.mouseX = 0
    this.mouseY = 0

    this.lastMouseX = 0
    this.lastMouseY = 0

    this.mouseOffX = 0
    this.mouseOffY = 0

    this.elmX = 0
    this.elmY = 0
    this.elmW = 0
    this.elmH = 0

    this.targetElement = null
  },
  mounted () {
    document.documentElement.addEventListener('mousemove', this.handleMove, true)
    document.documentElement.addEventListener('mousedown', this.deselect, true)
    document.documentElement.addEventListener('mouseup', this.handleUp, true)

    this.targetElement = this.target || this.$parent

    this.reviewDimensions()

    this.targetElement.$el.style.top = this.top + 'px'
    this.targetElement.$el.style.left = this.left + 'px'
    this.targetElement.$el.style.height = this.height + 'px'
    this.targetElement.$el.style.width = this.width + 'px'
  },
  beforeDestroy () {
    document.documentElement.removeEventListener('mousemove', this.handleMove, true)
    document.documentElement.removeEventListener('mousedown', this.deselect, true)
    document.documentElement.removeEventListener('mouseup', this.handleUp, true)
  },

  data () {
    return {
      top: this.y,
      left: this.x,
      width: this.w,
      height: this.h,
      resizing: false,
      dragging: false,
      enabled: this.active,
      currentHandle: null
    }
  },

  methods: {
    reviewDimensions () {
      if (this.minw > this.width) this.width = this.minw
      if (this.minh > this.height) this.height = this.minh
      if (this.parent) {
        this.parentW = parseInt(this.targetElement.$el.parentNode.clientWidth, 10)
        this.parentH = parseInt(this.targetElement.$el.parentNode.clientHeight, 10)
        if (this.width > this.parentW) this.width = this.parentW
        if (this.height > this.parentH) this.height = this.parentH
        if ((this.left + this.width) > this.parentW) this.width = this.parentW - this.left
        if ((this.top + this.height) > this.parentH) this.height = this.parentH - this.top
      }
      this.elmW = this.width
      this.elmH = this.height
      this.elmX = this.left
      this.elmY = this.top
      this.$emit('resizing', this.left, this.top, this.width, this.height)
    },
    elmDown (event) {
      if (this.$el.contains(event.target)) {
        if (
          (this.dragHandle && !event.target.matches(this.dragHandle)) ||
          (this.dragCancel && event.target.matches(this.dragCancel))) {
          return
        }
        event.stopPropagation()
        event.preventDefault()
        this.reviewDimensions()
        if (!this.enabled) {
          this.enabled = true
          this.$emit('activated')
          this.$emit('update:active', true)
        }
        if (this.draggable) {
          this.dragging = true
          this.$emit('dragstart')
        }
      }
    },
    deselect (event) {
      this.mouseX = event.pageX
      this.mouseY = event.pageY
      this.lastMouseX = this.mouseX
      this.lastMouseY = this.mouseY

      const regex = new RegExp('handle-([trmbl]{2})', '')

      if (!this.$el.contains(event.target) && !regex.test(event.target.className)) {
        if (this.enabled) {
          this.enabled = false
          this.$emit('deactivated')
          this.$emit('update:active', false)
        }
      }
    },
    handleDown (handle, adjustMousePosition = false) {
      this.currentHandle = handle
      this.resizing = true
      this.$emit('resizestart')
      if (adjustMousePosition) {
        this.mouseOffX = -this.minw
        this.mouseOffY = -this.minh
      }
    },
    handleMove: function (e) {
      this.mouseX = e.pageX
      this.mouseY = e.pageY

      let diffX = this.mouseX - this.lastMouseX + this.mouseOffX
      let diffY = this.mouseY - this.lastMouseY + this.mouseOffY

      this.mouseOffX = this.mouseOffY = 0
      this.lastMouseX = this.mouseX
      this.lastMouseY = this.mouseY

      const dX = diffX
      const dY = diffY

      if (this.resizing) {
        if (this.currentHandle.indexOf('t') >= 0) {
          if (this.elmH - dY < this.minh) this.mouseOffY = (dY - (diffY = this.elmH - this.minh))
          else if (this.parent && this.elmY + dY < this.parentY) this.mouseOffY = (dY - (diffY = this.parentY - this.elmY))
          this.elmY += diffY
          this.elmH -= diffY
        }

        if (this.currentHandle.indexOf('b') >= 0) {
          if (this.elmH + dY < this.minh) this.mouseOffY = (dY - (diffY = this.minh - this.elmH))
          else if (this.parent && this.elmY + this.elmH + dY > this.parentH) this.mouseOffY = (dY - (diffY = this.parentH - this.elmY - this.elmH))
          this.elmH += diffY
        }

        if (this.currentHandle.indexOf('l') >= 0) {
          if (this.elmW - dX < this.minw) this.mouseOffX = (dX - (diffX = this.elmW - this.minw))
          else if (this.parent && this.elmX + dX < this.parentX) this.mouseOffX = (dX - (diffX = this.parentX - this.elmX))
          this.elmX += diffX
          this.elmW -= diffX
        }

        if (this.currentHandle.indexOf('r') >= 0) {
          if (this.elmW + dX < this.minw) this.mouseOffX = (dX - (diffX = this.minw - this.elmW))
          else if (this.parent && this.elmX + this.elmW + dX > this.parentW) this.mouseOffX = (dX - (diffX = this.parentW - this.elmX - this.elmW))
          this.elmW += diffX
        }

        this.left = (Math.round(this.elmX / this.grid[0]) * this.grid[0])
        this.top = (Math.round(this.elmY / this.grid[1]) * this.grid[1])

        this.width = (Math.round(this.elmW / this.grid[0]) * this.grid[0])
        this.height = (Math.round(this.elmH / this.grid[1]) * this.grid[1])

        this.$emit('resizing', this.left, this.top, this.width, this.height)
      } else if (this.dragging) {
        if (this.parent) {
          if (this.elmX + dX < this.parentX) this.mouseOffX = (dX - (diffX = this.parentX - this.elmX))
          else if (this.elmX + this.elmW + dX > this.parentW) this.mouseOffX = (dX - (diffX = this.parentW - this.elmX - this.elmW))

          if (this.elmY + dY < this.parentY) this.mouseOffY = (dY - (diffY = this.parentY - this.elmY))
          else if (this.elmY + this.elmH + dY > this.parentH) this.mouseOffY = (dY - (diffY = this.parentH - this.elmY - this.elmH))
        }

        this.elmX += diffX
        this.elmY += diffY

        this.left = (Math.round(this.elmX / this.grid[0]) * this.grid[0])
        this.top = (Math.round(this.elmY / this.grid[1]) * this.grid[1])

        this.$emit('dragging', this.left, this.top)
      }
    },
    handleUp: function (e) {
      this.currentHandle = null
      if (this.resizing) {
        this.resizing = false
        this.$emit('resizestop', this.left, this.top, this.width, this.height)
      }
      if (this.dragging) {
        this.dragging = false
        this.$emit('dragstop', this.left, this.top)
      }
      this.elmX = this.left
      this.elmY = this.top
    }
  },

  watch: {
    // listen for prop changes to make component reactive
    active (val) {
      this.enabled = val
    },
    h (val) {
      this.height = val
      this.reviewDimensions()
    },
    w (val) {
      this.width = val
      this.reviewDimensions()
    },
    x (val) {
      this.left = val
      this.reviewDimensions()
    },
    y (val) {
      this.top = val
      this.reviewDimensions()
    },
    // put style on target
    top (val) {
      this.targetElement.$el.style.top = this.top + 'px'
    },
    left (val) {
      this.targetElement.$el.style.left = this.left + 'px'
    },
    width (val) {
      this.targetElement.$el.style.width = this.width + 'px'
    },
    height (val) {
      this.targetElement.$el.style.height = this.height + 'px'
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/uiColors';

.vdr {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  box-sizing: border-box;
}

.vdr /deep/ > * {
  pointer-events: auto;
}

.handle {
  box-sizing: border-box;
  display: none;
  position: absolute;
  width: 16px;
  height: 16px;
  background: $color-main-darker;
  border: 3px solid #fff;
  border-radius: 50%;
  z-index: z(boxEditorHandles);
}
.handle-tl {
  top: -8px;
  left: -8px;
  cursor: nw-resize;
}
.handle-tm {
  top: -8px;
  left: 50%;
  margin-left: -8px;
  cursor: n-resize;
}
.handle-tr {
  top: -8px;
  right: -8px;
  cursor: ne-resize;
}
.handle-ml {
  top: 50%;
  margin-top: -8px;
  left: -8px;
  cursor: w-resize;
}
.handle-mr {
  top: 50%;
  margin-top: -8px;
  right: -8px;
  cursor: e-resize;
}
.handle-bl {
  bottom: -8px;
  left: -8px;
  cursor: sw-resize;
}
.handle-bm {
  bottom: -8px;
  left: 50%;
  margin-left: -8px;
  cursor: s-resize;
}
.handle-br {
  bottom: -8px;
  right: -8px;
  cursor: se-resize;
}
</style>
